import { h } from '@omni-torn/ui';
import { battleCsv, changeSince, GYM_STATS, gymTotals, nextVisits, tctDay, type BattleVisits, type GymDay, type Snapshot } from '@omni-torn/shared';
import { ownHistoryOn } from '../own-history';
import { readBattleStats } from '../read';
import { history, server, session, torn } from '../session';
import { snapshotNow } from '../sync';
import { box, downloadText, kids, lineChart, loading, num, problem, short, signed, tabs, withTableCsv } from '../ui';
import { historyNote } from './shared';

const STATS = ['strength', 'defense', 'speed', 'dexterity'] as const;
type Stat = (typeof STATS)[number];
type Key = Stat | 'total';
const KEYS: readonly Key[] = [...STATS, 'total'];
const NAME: Record<Key, string> = { strength: 'Strength', defense: 'Defense', speed: 'Speed', dexterity: 'Dexterity', total: 'Total' };
const DAY = 86_400_000;
type Stats = Record<Key, number>;

/** Chart ranges: everything the server keeps (400 days) is "All". */
const RANGES: Array<{ id: string; label: string; days: number | null }> = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  { id: '365', label: '1 year', days: 365 },
  { id: 'all', label: 'All', days: null },
];
const GYM_RANGES = [
  { id: '1', label: 'Today' },
  { id: '7', label: '7 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
];

/**
 * "Since your last visit" — kept in this browser only (per device, like TornStats' "record"). A visit within 30 minutes
 * of the last one keeps the same starting point, so a reload doesn't reset it to ±0.
 */
const VISIT_KEY = (id: number) => `omni-torn.battle.visit.${id}`;
type Visits = BattleVisits<Stats>;
function visits(playerId: number, stats: Stats): Visits['prev'] {
  let v: Visits = { prev: null, cur: null };
  try {
    const raw = JSON.parse(localStorage.getItem(VISIT_KEY(playerId)) ?? 'null') as Visits | null;
    if (raw && typeof raw === 'object') v = { prev: raw.prev ?? null, cur: raw.cur ?? null };
  } catch {
    /* nothing kept here */
  }
  const next = nextVisits(v, stats, Date.now());
  try {
    localStorage.setItem(VISIT_KEY(playerId), JSON.stringify(next));
  } catch {
    /* this visit only */
  }
  return next.prev;
}

export async function renderBattle(view: HTMLElement): Promise<void> {
  const body = h('div', {}, loading('Reading your battle stats…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Battle stats'), h('p', { class: 'muted' }, 'Only you see these.')), body);
  let bs;
  try {
    bs = readBattleStats(await torn('/user/', { selections: 'battlestats' }));
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  if (!bs) {
    body.replaceChildren(h('p', { class: 'muted' }, 'Torn didn’t send your battle stats.'));
    return;
  }
  const b = bs;
  const me = session.me();
  const historyOn = me?.prefs.history !== false;
  let snaps: Snapshot[] = historyOn ? await history(400, 'stats').catch(() => []) : [];
  const nowStats: Stats = { strength: b.strength, defense: b.defense, speed: b.speed, dexterity: b.dexterity, total: b.total };
  const lastVisit = me ? visits(me.playerId, nowStats) : null;
  const ch = (k: Key, since: number) => changeSince(snaps, (s) => s.stats?.[k], since, b[k]);
  const sinceVisit = (k: Key) => (lastVisit ? b[k] - lastVisit.stats[k] : null);

  const tiles = h(
    'div',
    { class: 'tiles' },
    ...KEYS.map((k) => {
      const eff = k === 'total' ? null : b[k] * (1 + b.mods[k] / 100);
      const week = ch(k, Date.now() - 7 * DAY);
      const visit = sinceVisit(k);
      return h(
        'div',
        { class: `tile${k === 'total' ? ' tile-total' : ''}` },
        h('div', { class: 'tile-label' }, NAME[k]),
        h('div', { class: 'tile-value', title: num(b[k]) }, short(b[k])),
        k === 'total' ? h('div', { class: 'muted' }, num(b.total)) : h('div', { class: `muted ${b.mods[k as Stat] > 0 ? 't-green' : b.mods[k as Stat] < 0 ? 't-red' : ''}` }, `${b.mods[k as Stat] >= 0 ? '+' : ''}${b.mods[k as Stat]}% · ${Math.round((b[k] / b.total) * 100)}% of total`),
        eff !== null && b.mods[k as Stat] ? h('div', { class: 'muted' }, `in fights ≈ ${short(eff)}`) : null,
        visit !== null && visit !== 0 ? h('div', { class: `tile-change ${visit > 0 ? 't-green' : ''}`, 'data-visit': k }, `${signed(visit, short)} since last visit`) : null,
        week !== null ? h('div', { class: `tile-change ${week > 0 ? 't-green' : ''}` }, `${signed(week, short)} this week`) : null,
      );
    }),
  );

  // ---- gains: since your last visit, today, a week, a month, … all time
  const gainsHolder = h('div', {});
  const drawGains = () => {
    const now = Date.now();
    const cols: Array<{ label: string; title?: string; get: (k: Key) => number | null }> = [
      ...(lastVisit ? [{ label: 'Last visit', title: `Since ${new Date(lastVisit.at).toLocaleString()} (this browser)`, get: sinceVisit }] : []),
      { label: 'Today', title: 'Since 00:00 TCT', get: (k) => ch(k, tctDay(now)) },
      { label: '7 days', get: (k) => ch(k, now - 7 * DAY) },
      { label: '30 days', get: (k) => ch(k, now - 30 * DAY) },
      { label: '90 days', get: (k) => ch(k, now - 90 * DAY) },
      { label: '1 year', get: (k) => ch(k, now - 365 * DAY) },
      ...(snaps[0] ? [{ label: 'All time', title: `Since ${new Date(snaps[0].at).toLocaleDateString()} (the oldest kept)`, get: (k: Key) => ch(k, snaps[0]!.at) }] : []),
    ];
    const cell = (v: number | null) => h('td', { class: `r ${v && v > 0 ? 't-green' : v && v < 0 ? 't-red' : 'muted'}`, title: v === null ? '' : num(v) }, signed(v, short));
    gainsHolder.replaceChildren(
      withTableCsv(h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          { 'data-table': 'gains' },
          h('thead', {}, h('tr', {}, h('th', {}, ''), ...cols.map((c) => h('th', { class: 'r', title: c.title ?? '' }, c.label)))),
          h('tbody', {}, ...KEYS.map((k) => h('tr', {}, h('td', {}, NAME[k]), ...cols.map((c) => cell(c.get(k)))))),
        ),
      )),
    );
  };

  // ---- the chart: any stat, any range of what the server keeps
  let pick: Key = 'total';
  let range = '90';
  const chart = h('div', {});
  const drawChart = () => {
    const days = RANGES.find((r) => r.id === range)?.days ?? null;
    const from = days === null ? 0 : Date.now() - days * DAY;
    chart.replaceChildren(lineChart(snaps.filter((s) => s.stats && s.at >= from).map((s) => ({ at: s.at, v: s.stats![pick] })), { label: NAME[pick] }));
  };

  const recordStatus = h('span', { class: 'save-status muted small', 'data-record': 'status' });
  const recordBtn = h('button', { type: 'button', class: 'btn-small', title: 'Read your stats from Torn now (one call with your key) and keep them' }, 'Record now') as HTMLButtonElement;
  recordBtn.addEventListener('click', async (e) => {
    // Your own click only: one Torn call with your key per click.
    if (!e.isTrusted) return;
    recordBtn.disabled = true;
    recordStatus.textContent = 'Recording…';
    try {
      const kept = await snapshotNow();
      snaps = await history(400, 'stats').catch(() => snaps);
      recordStatus.textContent = kept ? `Recorded at ${new Date().toLocaleTimeString()}.` : 'Not kept (history is off?).';
      recordStatus.className = `save-status small ${kept ? 'ok' : 'bad'}`;
      drawGains();
      drawChart();
    } catch (err) {
      recordStatus.textContent = (err as Error).message;
      recordStatus.className = 'save-status small bad';
    } finally {
      recordBtn.disabled = false;
    }
  });
  const csvBtn = h('button', { type: 'button', class: 'btn-small', onclick: () => downloadText(`battle-stats-${new Date().toISOString().slice(0, 10)}.csv`, battleCsv(snaps)) }, 'Export CSV');

  drawGains();
  drawChart();

  // ---- gym gains vs. energy (from the kept log)
  const gymBox = h('div', {});
  void gymSection(gymBox);

  body.replaceChildren(
    ...kids(
      tiles,
      historyNote(snaps.length),
      historyOn
        ? box(
            'Gains',
            [
              h('p', { class: 'muted small' }, ownHistoryOn() ? 'How much each stat grew. “Last visit” is kept in this browser; the rest comes from your history (hourly, then daily; kept for you on Omni-Torn).' : 'How much each stat grew. “Last visit” is kept in this browser; the rest comes from your history (hourly, then daily; 400 days).'),
              gainsHolder,
            ],
            { id: 'gains', extra: h('span', { class: 'head-actions' }, recordStatus, ' ', recordBtn) },
          )
        : null,
      historyOn
        ? box(
            'History',
            [
              h(
                'div',
                { class: 'toolbar' },
                tabs(
                  KEYS.map((k) => ({ id: k, label: NAME[k] })),
                  pick,
                  (id) => {
                    pick = id as Key;
                    drawChart();
                  },
                ),
                tabs(
                  RANGES.map((r) => ({ id: r.id, label: r.label })),
                  range,
                  (id) => {
                    range = id;
                    drawChart();
                  },
                ),
              ),
              chart,
            ],
            { id: 'battle-history', extra: csvBtn },
          )
        : null,
      box('Gym: gains vs. energy', gymBox, { id: 'gym' }),
      b.info.length ? box('What changes them in fights', h('ul', { class: 'plain-list' }, ...b.info.map((i) => h('li', {}, i)))) : null,
    ),
  );
}

/** Energy spent in the gym and what it gave, per stat, from the log Omni-Torn keeps (Full key, background tracking). */
async function gymSection(holder: HTMLElement): Promise<void> {
  holder.replaceChildren(loading('Reading your gym trains…'));
  let days: GymDay[];
  let logs = 0;
  try {
    const r = await server<{ days: GymDay[]; logs: number }>('GET', '/api/me/gym?days=90');
    days = r.days;
    logs = r.logs;
  } catch (err) {
    holder.replaceChildren(problem(err));
    return;
  }
  if (!logs) {
    holder.replaceChildren(h('p', { class: 'muted', 'data-note': 'gym-no-log' }, 'Gym gains come from your activity log, which Omni-Torn keeps for you with a Full Access key and background tracking (Account). Then every train shows here: energy spent and what it gave.'));
    return;
  }
  let span = '30';
  const out = h('div', {});
  const draw = () => {
    const since = span === '1' ? tctDay(Date.now()) : Date.now() - Number(span) * DAY;
    const t = gymTotals(days, since);
    if (!t.trains) {
      out.replaceChildren(h('p', { class: 'muted' }, 'No gym trains in your kept log for this time.'));
      return;
    }
    const per100 = (g: number, e: number) => (e > 0 ? num((g / e) * 100, 1) : '—');
    out.replaceChildren(
      withTableCsv(h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          { 'data-table': 'gym' },
          h('thead', {}, h('tr', {}, h('th', {}, ''), h('th', { class: 'r' }, 'Energy'), h('th', { class: 'r' }, 'Gained'), h('th', { class: 'r' }, 'Per 100 energy'))),
          h(
            'tbody',
            {},
            ...GYM_STATS.map((s) => h('tr', {}, h('td', {}, NAME[s]), h('td', { class: 'r' }, num(t.energy[s])), h('td', { class: `r ${t.gain[s] > 0 ? 't-green' : 'muted'}` }, t.gain[s] ? `+${num(t.gain[s])}` : '—'), h('td', { class: 'r' }, per100(t.gain[s], t.energy[s])))),
            h('tr', { class: 'row-total' }, h('td', {}, 'Total'), h('td', { class: 'r' }, num(t.energyAll)), h('td', { class: 'r t-green' }, `+${num(t.gainAll)}`), h('td', { class: 'r' }, per100(t.gainAll, t.energyAll))),
          ),
        ),
      )),
      h('p', { class: 'muted small' }, `${num(t.trains)} trains. Gained per day:`),
      lineChart(
        days.filter((d) => d.day + DAY > since).map((d) => ({ at: d.day, v: GYM_STATS.reduce((a, s) => a + d.gain[s], 0) })),
        { label: 'Gained in the gym per day', height: 120 },
      ),
    );
  };
  holder.replaceChildren(
    tabs(GYM_RANGES, span, (id) => {
      span = id;
      draw();
    }),
    out,
    h('p', { class: 'muted small' }, ownHistoryOn() ? 'From your activity log kept for you on Omni-Torn.' : 'From your activity log kept by background tracking (90 days).'),
  );
  draw();
}
